import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ImageIcon, Pen, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SettingsConfirmDialog } from '../SettingsConfirmDialog';
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export function SignatureUpload({ currentUrl, onFilePicked, onRemove, placeholderIcon = 'pen', placeholderText, 
// Mirrors the backend cap (all three upload endpoints reject > 2MB); a
// stricter client default just bounces files the server would accept.
maxSizeKb = 2048, disabled = false, disabledHint, removeConfirmTitle, removeConfirmBody, }) {
    const inputRef = useRef(null);
    const { toast } = useToast();
    const { t } = useTranslation('settings');
    const [dragActive, setDragActive] = useState(false);
    const [confirmingRemove, setConfirmingRemove] = useState(false);
    // dragenter/dragleave also fire for every child element crossed, so a bare
    // boolean flickers off mid-hover; the depth counter only clears the
    // highlight when the pointer truly leaves the drop zone.
    const dragDepth = useRef(0);
    // Replace/Remove sit in a hover overlay only where hover is the way people
    // point: a mouse, with no touchscreen at all. Anywhere a finger can land
    // (phones, iPads, Windows touch laptops, which report hover:hover) they are
    // an always-visible row under the image, because an invisible overlay there
    // still takes taps: a tap on the image used to hit the unseen Remove.
    const canHover = useMediaQuery('(hover: hover)', true);
    const hasTouch = useMediaQuery('(any-pointer: coarse)', false);
    const hoverOverlay = canHover && !hasTouch;
    const label = placeholderText ?? t('reporting.signature.uploadSignature');
    // Shown in MB: "2 MB" reads better than "2048KB" in the hint and toast.
    const maxSizeMb = Math.round((maxSizeKb / 1024) * 10) / 10;
    const acceptFile = (file) => {
        if (!file)
            return;
        if (file.size > maxSizeKb * 1024) {
            toast({
                title: t('reporting.signature.fileTooLarge', { maxSize: maxSizeMb }),
                variant: 'destructive',
            });
            return;
        }
        // Browsers report SVG as image/svg+xml, but the backend (mirroring avatar
        // upload) only accepts JPEG/PNG/WebP. Keep the same allow-list here so
        // the user gets a friendly client-side message instead of a 400.
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast({ title: t('reporting.signature.invalidType'), variant: 'destructive' });
            return;
        }
        void onFilePicked(file);
    };
    const handleFileSelect = (e) => {
        acceptFile(e.target.files?.[0]);
        // Reset input so the same file can be re-selected
        e.target.value = '';
    };
    const handleDragEnter = (e) => {
        e.preventDefault();
        if (disabled)
            return;
        dragDepth.current += 1;
        setDragActive(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        if (disabled)
            return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0)
            setDragActive(false);
    };
    const handleDragOver = (e) => {
        // Required for the browser to allow a drop at all.
        e.preventDefault();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragActive(false);
        if (disabled)
            return;
        acceptFile(e.dataTransfer.files?.[0]);
    };
    const PlaceholderIconComponent = placeholderIcon === 'pen' ? Pen : ImageIcon;
    const triggerPicker = () => {
        if (disabled)
            return;
        inputRef.current?.click();
    };
    return (_jsxs("div", { className: "space-y-2", "data-testid": "upload-drop-zone", onDragEnter: handleDragEnter, onDragLeave: handleDragLeave, onDragOver: handleDragOver, onDrop: handleDrop, children: [_jsx("input", { ref: inputRef, type: "file", accept: "image/png,image/jpeg,image/webp", onChange: handleFileSelect, className: "hidden", disabled: disabled }), currentUrl ? (_jsxs("div", { className: "group relative", children: [_jsx("div", { className: cn('border-border flex min-h-[80px] items-center justify-center rounded-md border bg-white p-3', dragActive && 'border-primary ring-primary/40 ring-2'), children: _jsx("img", { src: currentUrl, alt: label, className: "max-h-[60px] max-w-full object-contain" }) }), !disabled && (_jsxs("div", { "data-testid": "signature-actions", "data-layout": hoverOverlay ? 'overlay' : 'row', className: cn('flex items-center gap-2', hoverOverlay
                            ? // Hidden layers must not take clicks either: pointer events
                                // only once hover or keyboard focus has revealed it.
                                'bg-background/80 pointer-events-none absolute inset-0 justify-center rounded-md opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
                            : 'pt-2'), children: [_jsx(Button, { variant: "outline", size: "sm", className: cn(hoverOverlay ? 'h-7 text-xs' : 'h-9 pointer-coarse:h-10'), onClick: triggerPicker, children: t('reporting.signature.replace') }), _jsxs(Button, { variant: "outline", size: "sm", className: cn('text-destructive hover:text-destructive gap-1.5', hoverOverlay ? 'h-7 text-xs' : 'h-9 pointer-coarse:h-10'), onClick: () => setConfirmingRemove(true), children: [_jsx(Trash2, { className: "h-3.5 w-3.5", "aria-hidden": true }), t('reporting.signature.remove')] })] }))] })) : (_jsxs("button", { type: "button", onClick: triggerPicker, disabled: disabled, className: cn('border-border bg-background/50 text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground disabled:hover:border-border disabled:hover:text-muted-foreground flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed py-5 transition-colors enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-60', dragActive && 'border-primary text-foreground bg-primary/5'), children: [_jsx(PlaceholderIconComponent, { className: "h-5 w-5" }), _jsx("span", { className: "text-xs", children: label }), _jsx("span", { className: "text-muted-foreground/60 text-[10px]", children: t('reporting.signature.dragDropHint') }), _jsx("span", { className: "text-muted-foreground/60 text-[10px]", children: t('reporting.signature.fileHint', { maxSize: maxSizeMb }) })] })), disabled && disabledHint && (_jsx("p", { className: "text-muted-foreground text-[11px] leading-snug", children: disabledHint })), _jsx(SettingsConfirmDialog, { open: confirmingRemove, title: removeConfirmTitle ?? t('reporting.signature.removeTitle'), description: removeConfirmBody ?? t('reporting.signature.removeBody'), confirmLabel: t('reporting.signature.remove'), cancelLabel: t('button.cancel', { ns: 'common' }), destructive: true, onCancel: () => setConfirmingRemove(false), onConfirm: () => {
                    setConfirmingRemove(false);
                    void onRemove();
                } })] }));
}
